import { API_URL } from 'config';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import './DashboardPage.css';
import { useNavigate } from 'react-router-dom';
import { Sidebar } from './components/Sidebar';
import { LicenseTab } from './components/LicenseTab';
import { ModulesTab } from './components/ModulesTab';
import { NewsTab } from './components/NewsTab';
import { PaymentModal } from './components/PaymentModal';
import { ReferralTab } from './components/ReferralTab';
import { FaqTab } from './components/FaqTab';
import { useLanguage } from '../../context/LanguageContext';

export const DashboardPage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState('key');
  const [toast, setToast] = useState({ message: '', type: 'error' });
  
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [paymentModal, setPaymentModal] = useState({ isOpen: false, type: '', item: '', data: null });
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    fetchLicenseData(true);
    fetchReferralInfo();
    fetchFaq();
    fetchPrices();
    
    const timeoutRef = fetchTimeout;
    
    const interval = setInterval(() => {
      fetchLicenseData(false);
    }, 30000);

    return () => {
      clearInterval(interval);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [licenseInfo, setLicenseInfo] = useState(null);
  const [hasKey, setHasKey] = useState(false);
  const [isActivated, setIsActivated] = useState(false);
  const [isExpired, setIsExpired] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);
  const [licenseType, setLicenseType] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);
  
  const [referralInfo, setReferralInfo] = useState({ code: '', balance: 0, discountPercent: 5, rewardPercent: 5, clicks: 0, transactions: [] });
  const [faqItems, setFaqItems] = useState([]);
  
  const isExpiredDeleted = useRef(false);
  const isFetching = useRef(false);
  const fetchTimeout = useRef(null);

  const [modules, setModules] = useState([
    { id: 'module_1', price: '345 $', owned: false },
    { id: 'module_2', price: '175 $', owned: false },
    { id: 'module_3', price: '105 $', owned: false },
    { id: 'module_4', price: '100 $', owned: false },
    { id: 'module_5', price: '150 $', owned: false },
  ]);

  const [prices, setPrices] = useState({ license_prices: { demo: 0, monthly: 50, permanent: 300 }, modules: {} });

  const triggerToast = useCallback((msg, type = 'error') => {
    setToast({ message: msg, type });
    setTimeout(() => setToast({ message: '', type: 'error' }), 3000);
  }, []);

  const handleFetchResponse = useCallback((response) => {
    if (response.status === 403) {
      localStorage.setItem('auth_check_block', (Date.now() + 300000).toString());
      navigate('/auth');
      return false;
    }
    return true;
  }, [navigate]);

  const fetchLicenseData = useCallback(async (force = false) => {
    if (isFetching.current) return;
    if (isExpiredDeleted.current && !force) return;

    const blockExpiry = localStorage.getItem('auth_check_block');
    const now = Date.now();

    if (blockExpiry && now < parseInt(blockExpiry)) {
      navigate('/auth');
      return;
    }

    isFetching.current = true;

    try {
      const response = await fetch(`${API_URL}/license/get-license?_t=${Date.now()}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });

      if (!handleFetchResponse(response)) {
        isFetching.current = false;
        return;
      }

      const data = await response.json();

      if (response.status === 200 && data.status === 'active' && data.licenses?.length > 0) {
        localStorage.removeItem('auth_check_block');
        localStorage.removeItem('demo_expired_alert_shown'); 
        const lic = data.licenses[0];
        setLicenseInfo(lic);
        setHasKey(true);
        setIsActivated(lic.is_active);
        setIsExpired(false);
        setIsBlocked(false);
        setLicenseType(lic.license_type || lic.type);
        setExpiresAt(lic.expires_at);
        isExpiredDeleted.current = false;

        setModules(prevModules => 
          prevModules.map(mod => ({
            ...mod,
            owned: lic.modules && lic.modules.includes(mod.id)
          }))
        );
      } else if (response.status === 423 || data.status === 'blocked') {
        setHasKey(true);
        setIsActivated(data.is_active || false);
        setIsExpired(false);
        setIsBlocked(true);
        setLicenseType(data.license_type || data.type);
        setExpiresAt(data.expires_at);
        setLicenseInfo(prev => ({
          key: prev?.key || '',
          type: data.license_type || data.type,
          expires_at: data.expires_at,
          modules: data.modules || []
        }));
        
        if (force) {
          triggerToast(data.detail || t('dashboard.messages.licenseBlocked'), 'error');
        }
      } else if (response.status === 402 || data.status === 'expired') {
        setHasKey(true);
        setIsActivated(data.is_active || false);
        setIsExpired(true);
        setIsBlocked(false);
        setLicenseType(data.license_type || data.type);
        setExpiresAt(data.expires_at);
        setLicenseInfo(prev => ({
          key: prev?.key || '',
          type: data.license_type || data.type,
          expires_at: data.expires_at,
          modules: data.modules || []
        }));
        
        if (force) {
          triggerToast(data.detail || t('dashboard.messages.licenseExpired'), 'warning');
        }
      } else if (response.status === 410 || data.status === 'demo_expired_deleted') {
        isExpiredDeleted.current = true;
        setLicenseInfo(null);
        setHasKey(false);
        setIsActivated(false);
        setIsExpired(false);
        setIsBlocked(false);
        setLicenseType(null);
        setExpiresAt(null);

        const isAlertShown = localStorage.getItem('demo_expired_alert_shown');
        if ((!isAlertShown || force) && force) {
          triggerToast(data.detail || t('license.errors.demo_expired_deleted'), 'warning');
          localStorage.setItem('demo_expired_alert_shown', 'true');
        }
      } else if (response.status === 404 || data.status === 'no_license' || data.status === 'not_found') {
        setLicenseInfo(null);
        setHasKey(false);
        setIsActivated(false);
        setIsExpired(false);
        setIsBlocked(false);
        setLicenseType(null);
        setExpiresAt(null);
      } else if (force) {
        triggerToast(data.detail || t('dashboard.messages.networkError'), 'error');
      }
    } catch (error) {
      console.error("Ошибка сети:", error);
      if (force) {
        triggerToast(t('dashboard.messages.networkError'), 'error');
      }
    } finally {
      isFetching.current = false;
      setIsReady(true);
    }
  }, [handleFetchResponse, navigate, triggerToast, t]);

  const fetchPrices = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/license/prices`, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Cache-Control': 'no-cache' }
      });
      const data = await response.json();
      if (data.status === 'ok') {
        setPrices({ license_prices: data.license_prices || {}, modules: data.modules || {} });
        setModules((prev) => prev.map((m) => ({
          ...m,
          price: data.modules[m.id]?.price ? `${data.modules[m.id].price} $` : m.price,
        })));
      }
    } catch (error) {
      console.error('Prices fetch error:', error);
    }
  }, []);

  const fetchReferralInfo = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/license/referral-info`, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Cache-Control': 'no-cache' }
      });
      if (!handleFetchResponse(response)) return;
      const data = await response.json();
      if (response.status === 200) {
        setReferralInfo({
          code: data.referral_code || '',
          balance: data.referral_balance || 0,
          discountPercent: data.discount_percent || 5,
          rewardPercent: data.reward_percent || 5,
          clicks: data.clicks || 0,
          transactions: data.transactions || []
        });
      }
    } catch (error) {
      console.error('Referral fetch error:', error);
    }
  }, [handleFetchResponse]);

  const fetchFaq = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/license/faq`, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Cache-Control': 'no-cache' }
      });
      if (!handleFetchResponse(response)) return;
      const data = await response.json();
      if (response.status === 200) {
        setFaqItems(data.items || []);
      }
    } catch (error) {
      console.error('FAQ fetch error:', error);
    }
  }, [handleFetchResponse]);

  useEffect(() => {
    fetchLicenseData(true);
    fetchReferralInfo();
    fetchFaq();
    fetchPrices();
    
    const timeoutRef = fetchTimeout;
    
    const interval = setInterval(() => {
      fetchLicenseData(false);
    }, 30000);

    return () => {
      clearInterval(interval);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCopyKey = useCallback(async () => {
    if (!licenseInfo?.key) return;
    try {
      await navigator.clipboard.writeText(licenseInfo.key);
      triggerToast(t('dashboard.messages.copyKeySuccess'), 'success');
    } catch (error) {
      triggerToast(t('dashboard.messages.copyKeyFailed'), 'error');
    }
  }, [licenseInfo, triggerToast, t]);

  const handleBuyKey = async ({ type, referralCode, promoCode }) => {
    try {
      const payload = { type };
      if (referralCode) payload.referral_code = referralCode;
      if (promoCode) payload.promo_code = promoCode;
      const response = await fetch(`${API_URL}/license/buy-key`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (!handleFetchResponse(response)) return;

      const data = await response.json();

      if (response.status === 201) {
        isExpiredDeleted.current = false;
        localStorage.removeItem('demo_expired_alert_shown'); 
        triggerToast(t('license.success.demo_activated'), 'success');
        setPaymentModal({ isOpen: false, type: '', item: '', data: null });
        fetchLicenseData(true);
      } else if (response.status === 200 && data.payment_url) {
        triggerToast(t('dashboard.messages.redirectingToPayment'), 'info');
        window.location.href = data.payment_url;
      } else {
        triggerToast(data.detail || t('license.errors.payment_init_failed'), 'error');
      }
    } catch (error) {
      triggerToast(t('dashboard.messages.networkError'), 'error');
    }
  };

  const handleBuyModule = async ({ moduleId, referralCode, promoCode }) => {
    try {
      const payload = { modules: [moduleId] };
      if (referralCode) payload.referral_code = referralCode;
      if (promoCode) payload.promo_code = promoCode;
      const response = await fetch(`${API_URL}/license/buy-modules`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (!handleFetchResponse(response)) return;

      const data = await response.json();

      if (response.status === 200 && data.status === 'demo_modules_added') {
        triggerToast(t('dashboard.messages.demoModulesAdded'), 'success');
        setPaymentModal({ isOpen: false, type: '', item: '', data: null });
        fetchLicenseData(true);
      } else if (response.status === 200 && data.payment_url) {
        triggerToast(t('license.success.modules_payment_redirect'), 'info');
        window.location.href = data.payment_url;
      } else {
        triggerToast(data.detail || t('license.errors.modules_payment_failed'), 'error');
      }
    } catch (error) {
      triggerToast(t('dashboard.messages.networkError'), 'error');
    }
  };

  const handleUpgradeDemo = (type) => {
    setPaymentModal({ isOpen: true, type: 'license', item: 'License Key', data: { preselectedType: type, mode: 'buy' } });
  };

  const handleUpgradeToPermanent = () => {
    setPaymentModal({ isOpen: true, type: 'license', item: 'License Key', data: { preselectedType: 'permanent', mode: 'upgrade' } });
  };

  const handleUpgradeToPermanentConfirm = async ({ referralCode, promoCode }) => {
    try {
      const payload = {};
      if (referralCode) payload.referral_code = referralCode;
      if (promoCode) payload.promo_code = promoCode;
      const response = await fetch(`${API_URL}/license/upgrade-to-permanent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (!handleFetchResponse(response)) return;

      const data = await response.json();

      if (response.status === 200 && data.payment_url) {
        triggerToast(t('dashboard.messages.redirectingToPayment'), 'info');
        window.location.href = data.payment_url;
      } else if (response.status === 200) {
        triggerToast(t('dashboard.messages.upgradeRequestSent'), 'success');
        setPaymentModal({ isOpen: false, type: '', item: '', data: null });
        fetchLicenseData(true);
      } else {
        triggerToast(data.detail || t('dashboard.messages.upgradeRequestFailed'), 'error');
      }
    } catch (error) {
      triggerToast(t('dashboard.messages.networkError'), 'error');
    }
  };

  const handleModuleClick = (mod) => {
    if (licenseType === 'demo') {
      handleBuyModule({ moduleId: mod.id });
    } else {
      setPaymentModal({ isOpen: true, type: 'module', item: mod.name, data: mod });
    }
  };

  if (!isReady) {
    return (
      <div className="dashboard-auth-loading">
        <div className="auth-loading-spinner" />
      </div>
    );
  }

  return (
    <div className="dashboard-layout">
      {toast.message && <div className={`alert-toast alert-toast--${toast.type}`}><i className={`fas ${toast.type === 'success' ? 'fa-check-circle' : toast.type === 'warning' ? 'fa-exclamation-triangle' : 'fa-exclamation-circle'}`}></i> {toast.message}</div>}

      <button 
        className="dashboard-menu-toggle dashboard-menu-toggle-right"
        onClick={() => setSidebarOpen(!sidebarOpen)}
        aria-label="Toggle sidebar"
      >
        <i className="fas fa-bars"></i>
      </button>

      {sidebarOpen && (
        <div className="dashboard-sidebar-overlay" onClick={() => setSidebarOpen(false)} />
      )}

      <Sidebar 
        activeTab={activeTab} 
        setActiveTab={(tab) => { setActiveTab(tab); setSidebarOpen(false); }} 
        hasKey={hasKey} 
        triggerToast={triggerToast}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      <main className="dashboard-content">
        {activeTab === 'key' && (
          <LicenseTab 
            isActivated={isActivated}
            isExpired={isExpired}
            isBlocked={isBlocked}
            hasKey={hasKey}
            licenseInfo={licenseInfo}
            licenseType={licenseType}
            expiresAt={expiresAt}
            modules={modules} 
            triggerToast={triggerToast}
            handleFetchResponse={handleFetchResponse}
            fetchLicenseData={() => fetchLicenseData(true)}
            onBuyKeyClick={() => setPaymentModal({ isOpen: true, type: 'license', item: 'License Key', data: null })}
            onUpgradeDemo={handleUpgradeDemo}
            onUpgradeToPermanent={handleUpgradeToPermanent}
            onCopyKey={handleCopyKey}
          />
        )}

        {activeTab === 'modules' && (
          <ModulesTab 
            modules={modules}
            licenseType={licenseType}
            onBuyClick={handleModuleClick} 
          />
        )}

        {activeTab === 'news' && <NewsTab />}

        {activeTab === 'referral' && (
          <ReferralTab
            referralCode={referralInfo.code}
            balance={referralInfo.balance}
            discountPercent={referralInfo.discountPercent}
            rewardPercent={referralInfo.rewardPercent}
            clicks={referralInfo.clicks}
            transactions={referralInfo.transactions}
            triggerToast={triggerToast}
          />
        )}

        {activeTab === 'faq' && <FaqTab items={faqItems} />}
      </main>

      <PaymentModal 
        modalData={paymentModal} 
        onClose={() => setPaymentModal({ isOpen: false, type: '', item: '', data: null })} 
        onSelectLicense={handleBuyKey}
        onUpgradeToPermanent={handleUpgradeToPermanentConfirm}
        onConfirmModulePay={handleBuyModule}
        prices={prices}
      />
    </div>
  );
};
